import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { injectNavigationEnd } from 'ngxtension/navigation-end';
import { injectParams } from 'ngxtension/inject-params';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { SideMenuModule } from '../side-menu/side-menu.module';
import { TopBar } from '../top-bar/top-bar.component';
import { ProductStore } from '../../../stores/product.store';
import { ProductMenus } from './product.menu';
import { IMenu } from '../../../stores/layout.store';

@Component({
  selector: 'app-product-layout',
  templateUrl: './product.layout.html',
  standalone: true,
  imports: [RouterModule, CommonModule, TopBar, SideMenuModule],
})
export default class ProductLayout implements OnDestroy {
  private readonly productStore = inject(ProductStore);
  private readonly router = inject(Router);

  product = this.productStore.product;
  productId = injectParams('productId');
  menus = ProductMenus;

  source$ = injectNavigationEnd();

  activeMenu = toSignal(
    this.source$.pipe(
      map(ev => ev.url),
      map(url => {
        const productId = this.productId();
        if (!productId) return undefined;

        const matches = ProductMenus.filter(menu => {
          if (!menu.href) return false;
          const fullHref = `/product/${productId}/${menu.href}`;
          return url.startsWith(fullHref);
        });

        if (matches.length === 0) return undefined;

        return matches.sort(
          (a, b) => (b.href?.length || 0) - (a.href?.length || 0)
        )[0];
      })
    )
  );

  activeSubMenu = toSignal(
    this.source$.pipe(
      map(ev => ev.url),
      map(url => {
        const productId = this.productId();
        if (!productId) return undefined;

        const allChildren = ProductMenus.flatMap(m => m.children || []);
        const matches = allChildren.filter(child => {
          if (!child.href) return false;
          const fullHref = `/product/${productId}/${child.href}`;
          return url.startsWith(fullHref);
        });

        if (matches.length === 0) return undefined;

        return matches.sort(
          (a, b) => (b.href?.length || 0) - (a.href?.length || 0)
        )[0];
      })
    )
  );

  header = computed(() => {
    const productName = this.productStore.product()?.name;
    const parent = this.activeMenu();
    const sub = this.activeSubMenu();

    if (!productName) return '상품 관리';

    if (parent && sub) {
      return `${productName} > ${parent.name} > ${sub.name}`;
    }

    if (parent) {
      return `${productName} > ${parent.name}`;
    }

    return productName;
  });

  constructor() {
    toObservable(this.productId)
      .pipe(
        filter((productId): productId is string => !!productId),
        distinctUntilChanged()
      )
      .subscribe(productId => {
        this.productStore.setProductId(productId);
      });
  }

  navigate(menu: IMenu) {
    const productId = this.productId();
    if (productId && menu.href) {
      this.router.navigate([`/product/${productId}/${menu.href}`]);
    }
  }

  goToProductList() {
    this.router.navigate(['/product']);
  }

  ngOnDestroy() {
    // ProductLayout을 벗어날 때 ProductStore 초기화
    this.productStore.clearProduct();
  }
}
